import { useMemo } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community'

// AG Grid v33+ requires explicit module registration and uses the Theming API
// (no CSS-file themes). Registering the all-community bundle at import is
// idempotent — PreviewTable.tsx does the same.
ModuleRegistry.registerModules([AllCommunityModule])

interface ResultsGridProps {
  columns: string[]
  rows: unknown[][]
  /** Columns rendered with a key badge in the header (primary keys). */
  pkColumns?: string[]
  height?: number | string
  emptyMessage?: string
}

/**
 * The row grid shared by the Data tab and the SQL console.
 *
 * Rows arrive as positional arrays (that is what the backend returns — a SQL
 * result has no natural object shape and duplicate column names are legal), so
 * they are indexed into objects here with the column POSITION as the field.
 * Two columns called `id` from a join would collide under a name-keyed map.
 */
export default function ResultsGrid({
  columns, rows, pkColumns = [], height = '100%',
  emptyMessage = 'No rows',
}: ResultsGridProps) {
  const pkSet = useMemo(
    () => new Set(pkColumns.map((c) => c.toLowerCase())),
    [pkColumns],
  )

  const colDefs = useMemo(() => columns.map((name, i) => ({
    field: `c${i}`,
    headerName: pkSet.has((name || '').toLowerCase()) ? `🔑 ${name}` : name,
    sortable: true,
    resizable: true,
    filter: true,
    minWidth: 90,
    flex: columns.length <= 6 ? 1 : undefined,
    // NULL must be visually distinct from the empty string — in a data browser
    // those mean different things and look identical otherwise.
    cellRenderer: (p: { value: unknown }) => {
      if (p.value === null || p.value === undefined) {
        return <span style={{ opacity: 0.45, fontStyle: 'italic' }}>NULL</span>
      }
      if (typeof p.value === 'boolean') return String(p.value)
      if (typeof p.value === 'object') return JSON.stringify(p.value)
      return String(p.value)
    },
  })), [columns, pkSet])

  const rowData = useMemo(
    () => rows.map((r) => {
      const o: Record<string, unknown> = {}
      r.forEach((v, i) => { o[`c${i}`] = v })
      return o
    }),
    [rows],
  )

  const isLight = typeof document !== 'undefined'
    && document.body.classList.contains('light-theme')

  if (!columns.length) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height, color: 'var(--color-text-secondary)', fontSize: '0.875rem',
      }}>
        {emptyMessage}
      </div>
    )
  }

  return (
    <div
      data-ag-theme-mode={isLight ? 'light' : 'dark'}
      style={{ height, width: '100%', minHeight: 0 }}
    >
      <AgGridReact
        theme={themeQuartz}
        rowData={rowData}
        columnDefs={colDefs}
        headerHeight={32}
        rowHeight={30}
        animateRows={false}
        suppressCellFocus={false}
        enableCellTextSelection
        overlayNoRowsTemplate={`<span style="color:var(--color-text-secondary);font-size:0.875rem">${emptyMessage}</span>`}
      />
    </div>
  )
}
